import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { SystemappDetailComponent } from '../detail/systemapp-detail.component';

interface SystemAppItem {
  id: string;
  name: string;
  description: string;
  createTime: number;
  updateTime: number;
}

@Component({
  selector: 'systemapp-list',
  templateUrl: './systemapp-list.component.html',
  styleUrls: ['./systemapp-list.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzTableModule,
    NzTagModule,
    NzSelectModule,
    NzPopconfirmModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzPaginationModule,
    NzDividerModule,
    NzDropDownModule,
    NzToolTipModule,
    NzAffixModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class SystemappListComponent implements OnInit {
  // 列表数据
  listOfData: SystemAppItem[] = [];
  // 加载状态
  loading = false;
  // 搜索关键词
  searchKeyword = '';
  // 分页数据
  pageIndex: number = 1;
  pageSize: number = 10;
  total: number = 0;
  refreshTime = Date.now();
  direction = 'desc';

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  constructor(
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // 加载数据
  loadData(): void {
    this.loading = true;
    const params = {
      page: this.pageIndex - 1,
      pageSize: this.pageSize,
      key: this.searchKeyword,
      direction: this.direction,
      refreshTime: this.refreshTime
    };

    this.httpService
      .post(ConstantUtil.JOB_SYSTEMAPP_V1_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.listOfData = res.data.items || [];
          this.total = res.data.totalCount || 0;
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('systemapp.list.load.failed'));
        }
      });
  }

  // 搜索
  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  // 重置搜索
  resetSearch(): void {
    this.searchKeyword = '';
    this.search();
  }

  // 页码变更
  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadData();
  }

  // 每页条数变更
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  // 删除
  deleteSystemApp(id: string): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('systemapp.delete.confirm.title'),
      nzContent: this.i18n.fanyi('systemapp.delete.confirm.content'),
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        this.httpService.post(`${ConstantUtil.JOB_SYSTEMAPP_V1_DELETE}/${id}`, {}).subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('systemapp.delete.success'));
            this.refreshTime = Date.now();
            this.loadData();
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('systemapp.delete.failed'));
          }
        });
      }
    });
  }

  addSystemApp(): void {
    const modalRef = this.modal.create({
      nzContent: SystemappDetailComponent,
      nzWidth: '1000px',
      nzFooter: null,
      nzClosable: false,
      nzData: {
        isAdd: true
      }
    });

    modalRef.afterClose.subscribe(result => {
      if (result) {
        this.refreshTime = Date.now();
        this.loadData();
      }
    });
  }

  editSystemApp(id: string): void {
    this.modal
      .create({
        nzContent: SystemappDetailComponent,
        nzWidth: '1000px',
        nzFooter: null,
        nzClosable: false,
        nzData: {
          systemAppId: id,
          isAdd: false
        }
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.refreshTime = Date.now();
          this.loadData();
        }
      });
  }
}
